/*
 * <twc-signup-picker> — membership plan toggle + checkout hand-off.
 *
 * Children (rendered by blocks/twc-signup-plans.liquid):
 *   [data-twc-plan]        plan tile buttons, exactly two expected; carry
 *                          data-variant-id + data-selling-plan-id
 *   [data-twc-get-started] the GET STARTED button (per-button pending state)
 *   [data-twc-error]       inline error line (role="alert")
 *
 * Host attributes (the same three the community islands carry):
 *   data-storefront-api-token    public Storefront API token, from the shop
 *                                metafield twc.storefront_api_token
 *   data-store-domain            shop.permanent_domain
 *   data-storefront-api-version  the Storefront API version
 *
 * Hand-off: GET STARTED runs ONE Storefront API `cartCreate` mutation that
 * carries the chosen variant with its selling plan and the join's cart
 * attributes, then sends the buyer to the `checkoutUrl` it returns. A cart
 * permalink cannot do this: it DROPS the selling plan. The Storefront cart
 * is a cart of its own, so the visitor's Online Store cart is left alone
 * and the checkout holds the membership line and nothing else.
 *
 * Cart attributes on the new cart:
 *   twc_referred_by  the captured referral code, only when one is held
 *   date_of_birth    'YYYY-MM-DD' when this page knows it
 *   purchase_type    never: a join is no gift, and a new cart carries no
 *                    stale gift marker to remove
 * The three are merged into one body through TwcCartAttributes.merge(); an
 * empty value is dropped from the mutation, because on the Ajax path an
 * empty value REMOVES the key, and the order must carry the same
 * attributes on both paths.
 *
 * Fallback: when cartCreate fails (no token on the host, a network error, a
 * GraphQL error or a userError) the picker runs the Ajax Cart API sequence
 * TwcCartAttributes.clearThenAdd() — /cart/clear.js, /cart/update.js with
 * the same merged body, /cart/add.js — and goes to /checkout. That path
 * writes the visitor's Online Store cart. A failure on the fallback shows
 * the inline error and does NOT redirect, so a partly written cart never
 * reaches checkout. (Losing the referral attribute in that abort is
 * acceptable collateral: the checkout extension re-reads the localStorage
 * code as a fallback anyway.)
 *
 * Early cart: the first human signal on the page (pointer, touch or key)
 * starts the cartCreate for the selected tile ahead of GET STARTED, and a
 * later tile change starts a fresh one; an abandoned Storefront cart costs
 * nothing. GET STARTED takes that cart when it was made for the same
 * variant, plan and attributes less than 30 minutes ago, waiting for it if
 * it is still in flight. Otherwise, or when the early create failed, the
 * hand-off above runs unchanged. The early create writes nothing to the
 * Online Store cart and publishes no event: twc:add_to_cart fires only on
 * GET STARTED. A bfcache return from checkout drops the cached cart.
 *
 * Timing: the cartCreate round trip is wrapped in performance.mark() /
 * performance.measure() ('twc-signup:cartCreate') and printed as one
 * console line, so a tester can read the split without a profiler.
 */
(function (global) {
  'use strict';

  if (!global.customElements || global.customElements.get('twc-signup-picker')) return;

  var DEFAULTS = {
    pendingText: 'Adding your membership…',
    errorGeneric: 'Something went wrong adding your membership. Please try again.',
    errorConfig: 'This membership isn’t quite ready yet — please try again shortly.',
  };

  var DEFAULT_VERSION = '2026-07';

  // How long an early cart stays usable; past this GET STARTED makes a new one.
  var EARLY_CART_TTL_MS = 30 * 60 * 1000;

  // The signals that a person, not a crawler or a prerender, is on the page.
  var HUMAN_SIGNALS = ['pointermove', 'pointerdown', 'touchstart', 'keydown'];

  // The mutation also selects the one line it created, so the join can
  // publish twc:add_to_cart with the same item shape the Ajax path reads
  // from /cart/add.js: Shopify's pixels never see this cart. It must not
  // select sellingPlanAllocation: the public token has no selling-plan read
  // scope, so that field answers a top-level ACCESS_DENIED error and every
  // join would take the fallback. The plan id comes from the tile instead.
  var CART_CREATE =
    'mutation TwcSignupCartCreate($input: CartInput!) {' +
    ' cartCreate(input: $input) {' +
    '  cart {' +
    '   checkoutUrl' +
    '   lines(first: 1) { nodes {' +
    '    quantity' +
    '    merchandise { ... on ProductVariant {' +
    '     id title price { amount currencyCode } product { id title }' +
    '    } }' +
    '   } }' +
    '  }' +
    '  userErrors { field message }' +
    ' }' +
    '}';

  // "gid://shopify/ProductVariant/123" → 123; a bare number passes through.
  function gidNumber(gid) {
    var text = String(gid == null ? '' : gid);
    var tail = text.slice(text.lastIndexOf('/') + 1);
    var n = parseInt(tail, 10);
    return isNaN(n) ? null : n;
  }

  /*
   * The { currency, value, items } core of the join's twc:add_to_cart
   * payload, from the cartCreate lines (see TwcCartAttributes.trackAdded).
   * Prices arrive as decimal strings in currency units. A response without
   * lines (an older selection, a trimmed reply) yields an empty core, so
   * the event still fires with the source and page. `sellingPlanId` is the
   * plan the page sent with the line; cartCreate rejects a membership line
   * without one, so a created line carries exactly that plan.
   */
  function linesCore(cart, sellingPlanId) {
    var nodes = (cart && cart.lines && cart.lines.nodes) || [];
    var items = [];
    var value = 0;
    var currency = null;
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i] || {};
      var variant = node.merchandise || {};
      var product = variant.product || {};
      var priceInfo = variant.price || {};
      var price = Number(priceInfo.amount) || 0;
      var quantity = Number(node.quantity) || 0;
      if (!currency && priceInfo.currencyCode) currency = priceInfo.currencyCode;
      items.push({
        variant_id: gidNumber(variant.id),
        product_id: gidNumber(product.id),
        title: product.title || null,
        variant_title: variant.title || null,
        price: price,
        quantity: quantity,
        selling_plan_id: sellingPlanId || null,
      });
      value += price * quantity;
    }
    if (!currency) {
      currency = (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || null;
    }
    return { currency: currency, value: Math.round(value * 100) / 100, items: items };
  }

  function routeRoot() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  function now() {
    return global.performance && global.performance.now ? global.performance.now() : Date.now();
  }

  function mark(name) {
    if (global.performance && global.performance.mark) global.performance.mark(name);
  }

  function measure(name, start, end) {
    if (!global.performance || !global.performance.measure) return;
    try {
      global.performance.measure(name, start, end);
    } catch (e) {
      // A browser without the mark (or with a different overload) keeps the
      // console line below; the measure is a bonus for the profiler.
    }
  }

  var Picker = function () {
    return Reflect.construct(HTMLElement, [], Picker);
  };
  Picker.prototype = Object.create(HTMLElement.prototype);
  Picker.prototype.constructor = Picker;

  Picker.prototype.connectedCallback = function () {
    var self = this;
    self._abort = new AbortController();
    var signal = self._abort.signal;

    self._tiles = Array.prototype.slice.call(self.querySelectorAll('[data-twc-plan]'));
    self._cta = self.querySelector('[data-twc-get-started]');
    self._error = self.querySelector('[data-twc-error]');
    self._ctaLabel = self._cta ? self._cta.textContent : '';
    self._busyOn = false;

    self._t = function (key) {
      var v = self.dataset[key];
      return v != null && v !== '' ? v : DEFAULTS[key] || '';
    };

    // Default selection: first tile (design selects ONE-BOTTLE). The parent
    // block paints the first tile's ring by CSS while no tile is pressed, so
    // this press confirms the same tile and the ring does not move.
    if (self._tiles.length) self._select(self._tiles[0]);

    self._tiles.forEach(function (tile) {
      tile.addEventListener(
        'click',
        function () {
          if (self._busyOn) return;
          self._select(tile);
        },
        { signal: signal }
      );
    });

    if (self._cta) {
      self._cta.addEventListener(
        'click',
        function () {
          self._start();
        },
        { signal: signal }
      );
    }

    // Back-forward cache: returning from checkout restores the frozen page —
    // release the pending state so the button is usable again, and drop the
    // early cart, which has been to checkout.
    global.addEventListener(
      'pageshow',
      function (event) {
        if (!event.persisted) return;
        self._early = null;
        self._busy(false);
      },
      { signal: signal }
    );

    // The first human signal starts the early cart (see the header). Its own
    // controller removes every listener after that one fire.
    self._human = false;
    self._early = null;
    self._signals = new AbortController();
    var once = function () {
      if (self._signals) self._signals.abort();
      self._human = true;
      self._prime();
    };
    HUMAN_SIGNALS.forEach(function (type) {
      global.document.addEventListener(type, once, { passive: true, signal: self._signals.signal });
    });
  };

  Picker.prototype.disconnectedCallback = function () {
    if (this._abort) this._abort.abort();
    if (this._signals) this._signals.abort();
  };

  Picker.prototype._select = function (tile) {
    this._selected = tile;
    this._tiles.forEach(function (t) {
      t.setAttribute('aria-pressed', t === tile ? 'true' : 'false');
    });
    this._showError('');
    if (this._human) this._prime();
  };

  /*
   * The /cart/add.js item for the selected tile, or null when the tile is
   * not wired to a variant yet (theme editor / staging).
   */
  Picker.prototype._item = function () {
    var tile = this._selected;
    var variantId = tile ? parseInt(tile.dataset.variantId, 10) : NaN;
    var sellingPlanId = tile ? parseInt(tile.dataset.sellingPlanId, 10) : NaN;
    if (!variantId || isNaN(variantId)) return null;
    var item = { id: variantId, quantity: 1 };
    if (sellingPlanId && !isNaN(sellingPlanId)) item.selling_plan = sellingPlanId;
    return item;
  };

  /*
   * The three checkout attributes a JOIN owns, merged into ONE
   * /cart/update.js body (a second `attributes` object on the Ajax path
   * would wipe the others). twc_referred_by carries the captured referral
   * code. date_of_birth carries the buyer's birth date as YYYY-MM-DD when
   * this page knows it. purchase_type goes out EMPTY: on the Ajax path that
   * REMOVES a gift marker left by an earlier gifting checkout in the same
   * cart session; the mutation drops the empty key, because its cart is new.
   */
  Picker.prototype._updateBody = function (attrs) {
    var updateBody = {};
    var code = global.TwcReferral && global.TwcReferral.get ? global.TwcReferral.get() : null;
    if (code) attrs.merge(updateBody, { twc_referred_by: code });
    attrs.merge(updateBody, { date_of_birth: attrs.dobIso(this), purchase_type: '' });
    return updateBody;
  };

  // What an early cart was made for: GET STARTED reuses it only on a match.
  function cartKey(item, updateBody) {
    return JSON.stringify([item.id, item.selling_plan || null, updateBody.attributes || {}]);
  }

  /*
   * Starts the early cartCreate for the selected tile unless a live one for
   * the same key is already held. The cached promise always RESOLVES: to
   * the created cart, or to null when the create failed, so GET STARTED can
   * wait on it and fall through without a rejection to handle.
   */
  Picker.prototype._prime = function () {
    var attrs = global.TwcCartAttributes;
    var item = this._item();
    if (this._busyOn || !attrs || !item) return;
    var updateBody = this._updateBody(attrs);
    var key = cartKey(item, updateBody);
    var held = this._early;
    if (held && held.key === key && !held.failed && Date.now() - held.startedAt < EARLY_CART_TTL_MS) return;

    var entry = { key: key, startedAt: Date.now(), result: null, failed: false };
    entry.promise = this._createCart(updateBody.attributes, item).then(
      function (created) {
        entry.result = created;
        return created;
      },
      function () {
        entry.failed = true;
        if (global.console && global.console.debug) {
          global.console.debug('twc-signup: early cartCreate failed');
        }
        return null;
      }
    );
    this._early = entry;
  };

  Picker.prototype._showError = function (message) {
    if (!this._error) return;
    this._error.textContent = message || '';
    this._error.hidden = !message;
  };

  Picker.prototype._busy = function (on) {
    this._busyOn = !!on;
    if (!this._cta) return;
    this._cta.disabled = !!on;
    this._cta.setAttribute('aria-busy', on ? 'true' : 'false');
    this._cta.classList.toggle('twc-signup-picker__cta--busy', !!on);
    this._cta.textContent = on ? this._t('pendingText') : this._ctaLabel;
  };

  /*
   * The Storefront API configuration the block put on the host. A blank
   * token or domain means the mutation cannot run; _start() then takes the
   * fallback at once.
   */
  Picker.prototype._storefront = function () {
    return {
      token: String(this.getAttribute('data-storefront-api-token') || '').trim(),
      domain: String(this.getAttribute('data-store-domain') || '').trim(),
      version: String(this.getAttribute('data-storefront-api-version') || '').trim() || DEFAULT_VERSION,
    };
  };

  /*
   * ONE cartCreate mutation. Resolves to { checkoutUrl, core } — the
   * checkout URL and the twc:add_to_cart core built from the cart's lines;
   * rejects on a missing configuration, a non-2xx response, a GraphQL
   * error, a userError or a response with no checkoutUrl. The caller takes
   * the fallback on any rejection, so this function never shows an error
   * itself.
   *
   * `attributes` is the merged object from TwcCartAttributes.merge(). An
   * empty value is dropped: on the Ajax path an empty value REMOVES the key,
   * and a new cart has no key to remove.
   */
  Picker.prototype._createCart = function (attributes, item) {
    var cfg = this._storefront();
    if (!cfg.token || !cfg.domain || !global.fetch) {
      return Promise.reject(new Error('storefront configuration missing'));
    }

    var line = { merchandiseId: 'gid://shopify/ProductVariant/' + item.id, quantity: item.quantity };
    if (item.selling_plan) line.sellingPlanId = 'gid://shopify/SellingPlan/' + item.selling_plan;

    var attrs = [];
    for (var key in attributes) {
      if (!Object.prototype.hasOwnProperty.call(attributes, key)) continue;
      var value = attributes[key];
      if (value == null || value === '') continue;
      attrs.push({ key: key, value: String(value) });
    }

    var endpoint = 'https://' + cfg.domain + '/api/' + cfg.version + '/graphql.json';
    var started = now();
    mark('twc-signup:cartCreate:start');

    function done() {
      mark('twc-signup:cartCreate:end');
      measure('twc-signup:cartCreate', 'twc-signup:cartCreate:start', 'twc-signup:cartCreate:end');
      if (global.console && global.console.info) {
        global.console.info('twc-signup: cartCreate ' + Math.round(now() - started) + ' ms');
      }
    }

    return global
      .fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token,
        },
        body: JSON.stringify({
          query: CART_CREATE,
          variables: { input: { lines: [line], attributes: attrs } },
        }),
      })
      .then(
        function (res) {
          done();
          if (!res.ok) throw new Error('cartCreate HTTP ' + res.status);
          return res.json();
        },
        function (err) {
          done();
          throw err;
        }
      )
      .then(function (body) {
        if (body.errors && body.errors.length) throw new Error('cartCreate GraphQL error');
        var payload = body.data && body.data.cartCreate;
        var userErrors = (payload && payload.userErrors) || [];
        if (userErrors.length) throw new Error('cartCreate userError');
        var cart = payload && payload.cart;
        var url = cart && cart.checkoutUrl;
        if (!url) throw new Error('cartCreate returned no checkoutUrl');
        return { checkoutUrl: url, core: linesCore(cart, item.selling_plan) };
      });
  };

  Picker.prototype._start = function () {
    var self = this;
    if (self._busyOn) return;
    self._showError('');

    var item = self._item();
    if (!item) {
      // Tile not wired up with a variant yet (theme editor / staging).
      self._showError(self._t('errorConfig'));
      return;
    }

    // TwcCartAttributes owns the merged attribute body and the fallback cart
    // write, so the join cannot run without it. The section loads it with
    // `defer` ahead of this file, which keeps the order — a missing helper
    // is a build fault, and the flow fails closed.
    var attrs = global.TwcCartAttributes;
    if (!attrs) {
      self._showError(self._t('errorGeneric'));
      return;
    }

    self._busy(true);

    var updateBody = self._updateBody(attrs);

    // The early cart, when it was made for this exact join and is fresh. A
    // pending one is awaited; a failed, stale or mismatched one is skipped.
    // It is taken once: a second press makes a cart of its own.
    var early = self._early;
    var reuse =
      early &&
      !early.failed &&
      early.key === cartKey(item, updateBody) &&
      Date.now() - early.startedAt < EARLY_CART_TTL_MS;
    if (reuse) self._early = null;

    // On both paths the order is: the cart write, then twc:add_to_cart from
    // what that write answered, then the ONE redirect once the bridge has
    // settled (bounded at ~300 ms). trackAdded never rejects, so a created
    // cart always reaches checkout; a failed write skips it, so an error
    // path never publishes.
    var cart = reuse
      ? early.promise.then(function (created) {
          return created || self._createCart(updateBody.attributes, item);
        })
      : self._createCart(updateBody.attributes, item);

    cart
      .then(
        function (created) {
          return attrs.trackAdded('signup', created.core).then(function () {
            global.location.href = created.checkoutUrl;
            // keep the pending state during navigation; pageshow releases it
            // if the visitor comes straight back.
          });
        },
        function () {
          // The Ajax fallback (see the header). The token never reaches the
          // console line.
          if (global.console && global.console.warn) {
            global.console.warn('twc-signup: cartCreate failed, using the cart API');
          }
          return attrs
            .clearThenAdd(routeRoot(), updateBody, [item])
            .then(function (body) {
              return attrs.trackAdded('signup', attrs.addedItems(body));
            })
            .then(function () {
              global.location.href = '/checkout';
            });
        }
      )
      .catch(function (err) {
        self._busy(false);
        var detail = err && err.data && (err.data.description || err.data.message);
        self._showError(typeof detail === 'string' && detail !== '' ? detail : self._t('errorGeneric'));
      });
  };

  global.customElements.define('twc-signup-picker', Picker);
})(window);
